/**
 * Icon Component
 *
 * A centralized icon component that provides automatic sizing and theming.
 * This component abstracts away the underlying icon library (Lucide) and
 * provides a consistent interface for rendering icons throughout the application.
 *
 * Features:
 * - Automatic sizing based on design tokens
 * - Automatic theme color integration
 * - Type-safe icon names via IconName enum
 * - Graceful fallback for missing icons
 * - Full passthrough of HTML/React props
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Icon name={IconName.DASHBOARD} />
 *
 * // With custom size
 * <Icon name={IconName.USER} size="lg" />
 *
 * // With custom color
 * <Icon name={IconName.ALERT_CIRCLE} color="red" />
 *
 * // With className and other props
 * <Icon name={IconName.SETTINGS} className="hover:opacity-80" aria-label="Settings" />
 * ```
 */

import { useIcon } from './useIcon';
import { useThemeColors } from '@/styles/colors/useThemeColors';
import { IconProps } from './types';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

/**
 * Icon component with automatic sizing and theming.
 *
 * @param name - The semantic icon name from IconName enum
 * @param size - Icon size ('sm' | 'md' | 'lg' | 'xl'), defaults to 'md'
 * @param color - Custom color override (uses theme color by default)
 * @param className - Additional CSS classes
 * @param props - Additional SVG props
 *
 * @returns Icon component or null if icon not found
 *
 * @example
 * ```tsx
 * // Dashboard icon with medium size
 * <Icon name={IconName.DASHBOARD} size="md" />
 *
 * // User icon with custom color
 * <Icon name={IconName.USER} color={colors.error} />
 *
 * // Save icon with large size and custom styling
 * <Icon
 *   name={IconName.SAVE}
 *   size="lg"
 *   className="cursor-pointer hover:scale-110 transition-transform"
 *   onClick={handleSave}
 * />
 * ```
 */
export function Icon({
  name,
  size = 'md',
  color,
  className,
  ...props
}: IconProps) {
  const { getIcon, getIconSize } = useIcon();
  const colors = useThemeColors();

  // Retrieve the icon component from registry
  const IconComponent = getIcon(name);

  // Handle missing icons gracefully
  if (!IconComponent) {
    if (process.env.NODE_ENV === 'development') {
      logger.ui.warn(
        `Icon component: Icon "${name}" not found. ` +
          `Rendering nothing. Add the icon to iconRegistry in icons/registry.ts`
      );
    }
    return null;
  }

  // Calculate pixel size based on design tokens and user sizing preference
  const pixelSize = getIconSize(size);

  // Use custom color or fall back to theme's primary icon color
  const iconColor = color || colors.icon.primary;

  return (
    <IconComponent
      size={pixelSize}
      color={iconColor}
      className={cn(className)}
      {...props}
    />
  );
}
